import * as React from 'react'
import debounce from '../util/debounce'

const canUseDOM = !!(
  typeof window !== 'undefined' &&
  window.document &&
  window.document.createElement
)

const _window = canUseDOM ? window : undefined

export type DimensionProps = { width: number; height: number }
export type ScreenDimensionProps = DimensionProps & {
  fontScale: number
  scale: number
}
export type DimensionContextProps = {
  screen: ScreenDimensionProps
  window: DimensionProps
  setWindow: (DimensionProps) => void
  setScreen: (ScreenDimensionProps) => void
}

const DimensionContext = React.createContext<DimensionContextProps>(null!)

export const useDimensions = () => React.useContext(DimensionContext)

export const DimensionsProvider: React.FC<{}> = (props) => {
  const [window, setWindow] = React.useState<DimensionProps>({
    width: _window!.innerWidth,
    height: _window!.innerHeight
  })
  const [screen, setScreen] = React.useState<ScreenDimensionProps>({
    fontScale: 1,
    height: _window!.screen.height,
    scale: _window!.devicePixelRatio || 1,
    width: _window!.screen.width
  })

  const _update = React.useCallback(() => {
    setWindow({
      height: _window!.innerHeight,
      width: _window!.innerWidth
    })

    setScreen({
      fontScale: 1,
      height: _window!.screen.height,
      scale: _window!.devicePixelRatio || 1,
      width: _window!.screen.width
    })
  }, [])

  const context = { screen, window, setScreen, setWindow }

  React.useEffect(() => {
    if (canUseDOM) {
      _window!.addEventListener('resize', debounce(_update, 16), false)

      return () => {
        _window!.removeEventListener('resize', debounce(_update, 16), false)
      }
    }
    return undefined
  }, [])

  return (
    <DimensionContext.Provider value={context}>
      {props.children}
    </DimensionContext.Provider>
  )
}
